import {
  forwardRef,
  useCallback,
  useState,
  type ChangeEvent,
  type FocusEvent,
  type InputHTMLAttributes,
} from 'react';

import { cn } from '../../lib/utils.js';

import { InputGroup, InputGroupIcon } from './input-group.js';
import { Input } from './input.js';

export type MoneyInputSize = 'sm' | 'md' | 'lg';

export interface MoneyInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'size'
> {
  valueCents: number;
  onValueCentsChange: (cents: number) => void;
  inputSize?: MoneyInputSize;
}

function formatCentsPlain(cents: number): string {
  const amount = Math.max(0, Math.round(cents));
  const whole = Math.floor(amount / 100);
  const frac = amount % 100;
  // Deterministic pt-BR (avoid Intl.toLocaleString — slow/flaky under vitest worker load).
  const wholeFormatted = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${wholeFormatted},${String(frac).padStart(2, '0')}`;
}

function parseCentsPlain(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return 0;
  }
  const normalized = trimmed
    .replace(/\./g, '')
    .replace(',', '.')
    .replace(/[^\d.-]/g, '');
  if (normalized.length === 0 || normalized === '-' || normalized === '.') {
    return null;
  }
  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return Math.round(parsed * 100);
}

/** BRL cents field with R$ prefix — protótipo MoneyInput / MoneyField. */
export const MoneyInput = forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { valueCents, onValueCentsChange, inputSize = 'md', className, onBlur, onFocus, ...props },
  ref,
) {
  const [draft, setDraft] = useState<string | null>(null);
  const display = draft ?? formatCentsPlain(valueCents);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value;
      setDraft(next);
      const parsed = parseCentsPlain(next);
      if (parsed !== null) {
        onValueCentsChange(Math.max(0, parsed));
      }
    },
    [onValueCentsChange],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      setDraft(formatCentsPlain(valueCents));
      // Replace-on-type: avoid appending digits into ",00" (which parses as +0.01).
      requestAnimationFrame(() => {
        event.target.select();
      });
      onFocus?.(event);
    },
    [onFocus, valueCents],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const parsed = parseCentsPlain(event.target.value);
      const cents = parsed === null ? 0 : Math.max(0, parsed);
      onValueCentsChange(cents);
      setDraft(null);
      onBlur?.(event);
    },
    [onBlur, onValueCentsChange],
  );

  return (
    <InputGroup hasIcon className={cn('money-input', `money-input--${inputSize}`, className)}>
      <InputGroupIcon>R$</InputGroupIcon>
      <Input
        ref={ref}
        className="mono"
        inputMode="decimal"
        value={display}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        {...props}
      />
    </InputGroup>
  );
});
